/**
 * The scroll tip's choreography: how the launcher arrives, how the card grows
 * out of it, and the ring that asks for the eye in between.
 *
 * Its own module for the reason `ribbonMotion` and `revealMotion` are: a file
 * exporting a component may not also export constants without breaking fast
 * refresh.
 *
 * **Why `motion` here when the ribbon insists on CSS.** `ribbonMotion` argues
 * at length that a JavaScript entrance is wrong for the ribbon, and it is —
 * the ribbon is in the prerendered HTML and is the first thing on the page, so
 * a runtime entrance would either hold it blank or pop it in late. The tip is
 * the exact opposite case: it is in no prerendered page, it cannot appear
 * until a scroll event has happened, and by then the runtime has long since
 * arrived on an idle network. Nothing is being held back waiting for it.
 *
 * It also needs the one thing CSS keyframes cannot do here — an *exit*. The
 * launcher and the card trade places at the same corner, and that swap is the
 * whole illusion: a cut would leave two separate objects blinking on and off.
 *
 * The pulse is the exception and is CSS, because it is the one piece that
 * repeats on a cadence rather than playing once on a state change. A looping
 * `motion` animation would hold a JavaScript timer open for as long as the
 * visitor stays on the page; a keyframe with an iteration count stops itself
 * and costs nothing while it waits.
 *
 * ## Reduced motion
 *
 * `MotionProvider` sets `reducedMotion="user"`, which drops the transforms
 * site-wide and keeps the opacity — so every `motion` recipe below degrades to
 * a clean fade on its own.
 *
 * That provider has no reach into a CSS keyframe, so the pulse is covered
 * twice over: `theme/components.ts` clamps every animation on the site to
 * 0.01ms under `prefers-reduced-motion` (which lands the ring on its final
 * frame, and the ring's final frame is transparent), and `nudgePulseSx`
 * carries its own rule on top. Belt and braces, the same way `Sheen` does it —
 * the global clamp is a safety net for animations nobody audited, not a reason
 * for a new one to skip saying what it wants.
 */

/**
 * The house decelerating curve, and deliberately the same four numbers as
 * `RIBBON_EASING`. The tip and the ribbon are the two pieces of chrome that
 * arrive on their own rather than being scrolled to, and they should move
 * like the same website.
 */
export const NUDGE_EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

/** Exits accelerate away: the eye should not be asked to follow them out. */
const NUDGE_EXIT_EASE = [0.4, 0, 1, 1] as [number, number, number, number];

/**
 * The overshoot, and the one curve here that leaves the 0–1 range.
 *
 * `1.56` on the second control point is what carries the value past its target
 * and back — the thing that makes an object read as having mass rather than as
 * a layer whose opacity was turned up. It is applied only to the two entrances
 * a visitor is meant to notice (the launcher arriving, the card opening) and
 * never to an exit, where an overshoot would mean a thing growing slightly on
 * its way out.
 *
 * Kept at 1.56 rather than anything springier because the card is 360px of
 * text: past about this much, the glyphs visibly scale and the overshoot stops
 * reading as weight and starts reading as a wobble.
 */
const NUDGE_OVERSHOOT_EASE = [0.34, 1.56, 0.64, 1] as [number, number, number, number];

/**
 * 280ms in, 160ms out, 420ms for the two entrances that overshoot.
 *
 * Entrances for a card sit at 200–300ms before they start reading as sluggish,
 * and an overshoot needs longer than a plain ease because the budget has to
 * cover the travel *and* the settle — 420ms is about 280ms of arrival with the
 * return folded in on top. Exits stay shortest: a thing leaving should not take
 * as long as a thing arriving, because nobody is reading it on the way out.
 */
const IN = { duration: 0.28, ease: NUDGE_EASE };
const OUT = { duration: 0.16, ease: NUDGE_EXIT_EASE };
const SETTLE = { duration: 0.42, ease: NUDGE_OVERSHOOT_EASE };

/**
 * How long the launcher is alone on screen before the card opens itself.
 *
 * This is the two-beat arrival, and the delay is the entire point of it. The
 * card used to appear cold in the bottom-left corner — the lowest-attention
 * region of a page — and the complaint that started this rework was that
 * nobody noticed it had happened. A 48px accent disc that pings first gives
 * the eye somewhere to go, so by the time the words arrive the visitor is
 * already looking at the place they arrive in.
 *
 * 700ms because it has to clear the launcher's own 420ms settle (two things
 * moving at once in the same corner is one blur) and still land inside the
 * window where the eye is on the corner it was just drawn to. Longer and the
 * two beats stop being one event.
 */
export const NUDGE_CARD_LEAD_IN_MS = 700;

/**
 * The launcher: the disc that lands first, and that the card is dropped onto.
 *
 * It starts at a third of its size rather than at nothing, because a disc that
 * scales from zero reads as a dot being drawn and this one is meant to read as
 * an object arriving.
 *
 * Its exit is a shrink rather than a fade in place: the card takes over the
 * same corner in the same frames (`AnimatePresence` in its default sync mode),
 * so the disc collapsing as the card comes down on it reads as the card
 * landing rather than as two objects cross-fading. It is also behind the card
 * in paint order, so the last of it is hidden under the card regardless —
 * which is why no delay is needed here to hold it until the impact.
 */
export const nudgeLauncherMotion = {
  initial: { opacity: 0, scale: 0.34, y: 8 },
  animate: { opacity: 1, scale: 1, y: 0, transition: SETTLE },
  exit: { opacity: 0, scale: 0.6, transition: OUT },
} as const;

/* ----------------------------------------------------- the card's arrival */

/**
 * The drop: how far above its resting place the card starts, and how high it
 * returns after each of its two impacts.
 *
 * **Why 64px and not the top of the window.** The gesture asked for was a card
 * falling the full height of the screen onto the launcher. The bounce is the
 * good part of that idea and this keeps it exactly; the 800px of travel is the
 * part that costs, for three reasons specific to this component rather than
 * matters of taste.
 *
 * - The tip sits at `zIndex: speedDial` (1050), deliberately *below* `appBar`
 *   (1100), so a card falling from the top of the window would spend its first
 *   hundred pixels sliding **under** the sticky header and the promise ribbon.
 * - It would sweep the column of page content it fell through — and on a phone
 *   the card is near full width, so that is the whole screen. Not doing that is
 *   the entire reason the tip is a corner card.
 * - A full drop plus two bounces runs about a second, on top of the 700ms
 *   lead-in, on as many as sixteen pages in a session.
 *
 * 64px is a little more than the launcher's own height — the distance that
 * reads as "dropped onto that" rather than "nudged", while keeping the whole
 * move inside the corner the card already occupies. The rebounds decay hard,
 * 20px then 6px: a bounce returning much more than a third of its height reads
 * as rubber, and this is a card with the company's name on it.
 */
const DROP_PX = 64;
const REBOUND_1_PX = 20;
const REBOUND_2_PX = 6;

/**
 * Gravity, in two curves.
 *
 * A fall accelerates and a rebound decelerates, and having these the right way
 * round is the whole difference between a bounce and a wobble. They are applied
 * per segment, so each leg is eased on its own — one ease spread across the
 * whole keyframe set would make the card drift into its impacts and snap out
 * of them, which is exactly backwards.
 */
const FALL_EASE: [number, number, number, number] = [0.4, 0, 1, 1];
const RISE_EASE: [number, number, number, number] = [0, 0, 0.2, 1];

/** Where each keyframe lands inside the drop. Five segments, six stops. */
const DROP_TIMES = [0, 0.39, 0.54, 0.73, 0.84, 1];

/** How long the drop takes, for everything that has to wait for it. */
export const NUDGE_CARD_DROP_MS = 560;

/**
 * The card: dropped onto the launcher, bounced twice, settled.
 *
 * `y` only, and no scale. The card used to grow out of the disc, and a drop
 * that also scaled would be telling two stories about where it came from at
 * once. `transformOrigin: bottom left` stays because the *exit* still scales,
 * and it has to collapse back toward the disc it is handing over to.
 *
 * Opacity is spent during the fall rather than at the end, so the card is
 * fully painted by the moment it lands — the landing is what the eye is being
 * drawn to, and there is nothing to gain from it being drawn to a half-faded
 * object.
 *
 * Transform and opacity throughout, so the whole thing stays on the compositor
 * and never touches layout.
 *
 * A spring was the obvious alternative and is the wrong tool here: a spring has
 * no duration, it settles — so on a slow phone it settles slowly and visibly,
 * and the number of bounces is whatever the physics happens to produce. Two
 * bounces was the brief, and keyframes are how you get exactly two.
 */
export const nudgeCardMotion = {
  initial: { opacity: 0, y: -DROP_PX },
  animate: {
    opacity: [0, 1, 1, 1, 1, 1],
    y: [-DROP_PX, 0, -REBOUND_1_PX, 0, -REBOUND_2_PX, 0],
    transition: {
      duration: NUDGE_CARD_DROP_MS / 1000,
      times: DROP_TIMES,
      ease: [FALL_EASE, RISE_EASE, FALL_EASE, RISE_EASE, FALL_EASE],
    },
  },
  exit: { opacity: 0, scale: 0.86, y: 8, transition: OUT },
  style: { transformOrigin: 'bottom left' },
};

/**
 * The phone's collapsed offer: the same move, shorter and without the
 * overshoot.
 *
 * 12px rather than 24, because the pill is about 64px tall and a rise of half
 * its own height reads as a drawer being thrown rather than offered. No scale
 * and no overshoot at all: at this height the bounce that flatters the card is
 * a bar that visibly stretches, which is the ribbon's own argument against
 * overshooting small type applied to a small box.
 */
export const nudgePeekMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: IN },
  exit: { opacity: 0, y: 8, transition: OUT },
} as const;

/**
 * The card's contents, arriving in order behind the box they sit in.
 *
 * A variant container rather than one animation on the whole block, so the
 * eyebrow, the headline, the body and the buttons land as a short cascade
 * instead of as one slab. That ordering is what makes a card read as having
 * been composed for the reader — it also puts the CTA last, which is the one
 * place in the card where arriving last is worth something.
 *
 * 40ms between children and 80ms behind the box: fast enough that the whole
 * cascade is over inside the card's own drop, so nothing is still moving by
 * the time the sheen crosses it. Slower staggers look deliberate in a hero
 * band and look like a slow page in a 360px card.
 *
 * The contents deliberately do **not** bounce with the box. The card is what
 * was dropped; the words are on it, not in it. Giving the type its own
 * rebound would make the card look like a container with loose contents, and
 * at this size two objects bouncing out of phase reads as a glitch.
 */
export const nudgeDetailMotion = {
  initial: 'hidden',
  animate: 'shown',
  variants: {
    hidden: {},
    shown: { transition: { delayChildren: 0.08, staggerChildren: 0.04 } },
  },
} as const;

/** One line of the cascade above. Spread onto each child of the container. */
export const nudgeDetailItemMotion = {
  variants: {
    hidden: { opacity: 0, y: 8 },
    shown: { opacity: 1, y: 0, transition: IN },
  },
} as const;

/**
 * When the one-pass sheen crosses the open card.
 *
 * Derived from the drop rather than written out, because the two have to stay
 * in agreement: the sheen exists to catch a *settled* card, and light crossing
 * something that is still moving reads as a rendering fault — the reason
 * `SHEEN_DELAY_MS` exists at all. The last rebound lands at the end of the
 * drop, so the pass starts one short beat after that, by which point the
 * content cascade behind it has also finished.
 */
export const NUDGE_SHEEN_DELAY_MS = NUDGE_CARD_DROP_MS + 80;

/* -------------------------------------------------------------- the pulse */

/** The name under which `nudgePulseSx` registers the ring. */
const NUDGE_PULSE_ANIMATION = 'kbxTipPulse';

/**
 * One ping: an outline the size of the glyph, expanding past it and fading.
 *
 * It is a ring and not a glow because a glow on a brand-red disc is a disc
 * that looks slightly out of focus, where an outline leaving the glyph is read
 * as something radiating from it.
 *
 * **Outlined and not filled**, which is the one detail that has to be right.
 * A filled disc on `::after` paints after its parent's children in tree order,
 * so for the first frames — while it is still no larger than the glyph — it
 * would lie a 45% red wash straight over the white lightbulb and visibly dim
 * it. The obvious fix, `zIndex: -1`, is worse: it would drop the ring behind
 * the nearest stacking context, which on the phone's collapsed pill is the
 * pill's own paper, and the ring would never be seen at all. A transparent
 * centre with a 2px rim only ever paints at its own edge, starting on the
 * disc's rim and travelling outwards, so there is no frame in which it covers
 * the glyph.
 *
 * The scale stops at 1.8 — large enough to be caught in peripheral vision,
 * small enough that it stays inside the 16/24px margin the wrapper already has
 * and never clips against the viewport edge. The rim thickens with the scale,
 * which is a transform doing what transforms do and reads correctly here: a
 * ring losing definition as it leaves.
 *
 * It ends fully transparent and at rest, so a visitor who arrives after the
 * last pass sees exactly what a visitor who never had one sees.
 */
const PING_FRAMES = {
  from: { transform: 'scale(1)', opacity: 0.5 },
  to: { transform: 'scale(1.8)', opacity: 0 },
} as const;

/** How long one ping takes to leave the glyph. */
export const NUDGE_PULSE_PING_MS = 1400;

/**
 * The gap between pings while the phone's collapsed pill waits to be opened.
 *
 * Long enough that it is a periodic reminder rather than a flashing object —
 * three seconds of ring and six of stillness is the cadence a notification
 * badge gets away with, and anything faster is the thing this feature is
 * trying not to be.
 */
const NUDGE_PULSE_CADENCE_MS = 6000;

/**
 * How many times the pill is allowed to ping before it gives up.
 *
 * It is capped, and the cap is the whole reason a repeating animation is
 * acceptable here at all. Three passes is roughly eighteen seconds of asking;
 * after that the visitor has either seen it or decided, and a ring still
 * going on minute two is a nag that cannot be dismissed because it is not a
 * control. Nothing on the site loops without a ceiling.
 */
const NUDGE_PULSE_PASSES = 3;

/**
 * Where in the cadence the visible ping sits, as a percentage.
 *
 * Derived rather than written out, because the two numbers have to agree: the
 * keyframes below span the whole 6s cadence, so the ping has to be squeezed
 * into the opening slice of it to keep travelling at `NUDGE_PULSE_PING_MS`.
 * Hard-coding "23%" would silently change the ping's speed the moment either
 * constant moved.
 */
const PING_END_PCT = `${((NUDGE_PULSE_PING_MS / NUDGE_PULSE_CADENCE_MS) * 100).toFixed(2)}%`;

/** A single ping filling its whole duration: the launcher's one-off. */
const onceKeyframes = { '0%': PING_FRAMES.from, '100%': PING_FRAMES.to } as const;

/** A ping and then stillness, repeated: the pill's cadence. */
const cadenceKeyframes = {
  '0%': PING_FRAMES.from,
  [PING_END_PCT]: PING_FRAMES.to,
  '100%': PING_FRAMES.to,
} as const;

/** Which of the two cadences a glyph is pinging on. */
export type NudgePulse =
  /** One ping, as the launcher lands and before the card opens over it. */
  | 'once'
  /** Three pings six seconds apart, while a collapsed pill waits. */
  | 'waiting';

/**
 * The ring, as an `sx` fragment to drop onto a glyph.
 *
 * It paints on `::after` rather than on a real element, so the ring is
 * decorative throughout: no pointer events, nothing in the accessibility tree,
 * and nothing for the button underneath it to have to lay out around. The
 * glyph it is spread onto supplies `position: relative` and the shape.
 *
 * Carries its own reduced-motion rule, which `Sheen` also does and for the
 * same reason: `MotionProvider`'s `reducedMotion="user"` governs `motion`
 * recipes and has no reach into a CSS keyframe. A visitor who asked for less
 * motion gets the glyph and no ring, which is the resting state anyway.
 */
export function nudgePulseSx(pulse: NudgePulse | false) {
  if (!pulse) return {};

  const once = pulse === 'once';

  return {
    [`@keyframes ${NUDGE_PULSE_ANIMATION}`]: once ? onceKeyframes : cadenceKeyframes,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      border: 2,
      borderColor: 'primary.main',
      opacity: 0,
      pointerEvents: 'none',
      animation: [
        NUDGE_PULSE_ANIMATION,
        `${once ? NUDGE_PULSE_PING_MS : NUDGE_PULSE_CADENCE_MS}ms`,
        'cubic-bezier(0.22, 1, 0.36, 1)',
        `${once ? 1 : NUDGE_PULSE_PASSES}`,
        'both',
      ].join(' '),
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&::after': { animation: 'none', opacity: 0 },
    },
  } as const;
}

/* ------------------------------------------------------- the deck turning */

/**
 * How far a slide travels as the deck turns, in pixels.
 *
 * Small on purpose, and much smaller than a hero carousel's full-width push.
 * This is one 360px card that has already arrived: the visitor knows where it
 * is and the movement only has to say "that was a different message", not
 * "you are somewhere else now". A slide that travels its own width inside a
 * card that is not moving reads as the words having come loose from it, and at
 * this size the whole journey happens inside the reader's fixation anyway.
 *
 * 24px is also comfortably inside the card's 48px of horizontal padding, so
 * nothing is ever seen crossing the brand-red edge on its way in.
 */
const NUDGE_SLIDE_SHIFT = 24;

/**
 * One slide of the deck arriving and the one before it leaving.
 *
 * Spread onto an `m.div` keyed on the slide index, inside an `AnimatePresence`
 * with `mode="wait"` — and `wait` here rather than the sync mode the stage
 * swap uses, which is the opposite choice to the one `ScrollNudge` makes two
 * files up. The reason is what the two are doing. The stages overlap because
 * the card has to be seen landing *on* the disc, so both must be on screen at
 * once. The slides must not overlap, because two copies of the body copy
 * crossing each other in a 360px box is unreadable for the 160ms it takes —
 * and because the card's `aria-labelledby` points at one headline id, which
 * would be ambiguous with two slides mounted.
 *
 * `direction` is which way the deck turned: `1` forward, `-1` back. The
 * arriving slide comes from the side the deck is heading toward and the
 * leaving one goes the other way, so the movement agrees with the control that
 * caused it. Getting this backwards is not subtle — pressing "previous" and
 * watching the content move forward is actively disorienting, and it is the
 * single most common fault in hand-rolled carousels.
 *
 * Opacity does the real work and the shift is the garnish, which is what makes
 * this safe under `prefers-reduced-motion`: `MotionConfig reducedMotion="user"`
 * drops the transform and keeps the fade, leaving a plain crossfade rather
 * than an instant cut. (The rotation itself also stops asking in that case —
 * see `hooks/useTipCarousel.ts`.)
 */
export function nudgeSlideMotion(direction: 1 | -1) {
  return {
    initial: { opacity: 0, x: direction * NUDGE_SLIDE_SHIFT },
    animate: { opacity: 1, x: 0, transition: IN },
    exit: { opacity: 0, x: direction * -NUDGE_SLIDE_SHIFT, transition: OUT },
  } as const;
}
